import { Box, Checkbox, Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { selectableOptionSx } from "@zfp-akademie-a-s/zfp-design";
import { NumberField } from "@zfp-akademie-a-s/zfp-design/form/number-field";
import { getListInsuranceContractsQueryKey, useUpdateInsuranceContract } from "Infrastructure/ApiZfp/AnalyzerApi";
import type { Contract } from "Infrastructure/ApiZfp/Schema";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { getAmountFrequencyTranslation } from "Infrastructure/Utils/LanguageUtils";
import { useContext } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { AnalysisDetailContext } from "../Detail/AnalysisDetailContext";

type Props = {
  readonly clientId: string;
  readonly insuranceContract: Contract;
  readonly partnerName: string;
  readonly selected?: boolean;
  readonly onEdit?: () => void;
};

export const ClientSolutionTile: React.FC<Props> = ({ clientId, insuranceContract, selected, partnerName, onEdit }) => {
  const { t } = useResource();
  const { readOnly } = useContext(AnalysisDetailContext);
  const { analysisId, versionId } = useParams() as {
    analysisId: string;
    versionId: string;
  };

  const queryClient = useQueryClient();

  const { mutateAsync: updateInsuranceContract } = useUpdateInsuranceContract({
    mutation: {
      onSuccess: () => {
        toast.success(t(Resources.AnalysisDetail.Insurance.InsuranceContractEdited));
        queryClient.invalidateQueries({ queryKey: getListInsuranceContractsQueryKey(analysisId, versionId, clientId) });
      },
    },
  });

  return (
    // the package's selectable look, tinted while its checkbox is ticked; a click elsewhere opens the contract
    <Stack
      spacing={Spacing.M}
      sx={[selectableOptionSx, { borderColor: "divider", borderRadius: 2, p: 4 }]}
      onClick={() => {
        onEdit?.();
      }}
    >
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" fontWeight={600}>
            {partnerName}
          </Typography>
          <Checkbox
            sx={{ p: 0 }}
            checked={!!selected}
            onClick={async (event) => {
              try {
                event.stopPropagation();

                const { guid, ...contractBody } = insuranceContract;
                await updateInsuranceContract({
                  analysisId,
                  versionId,
                  clientGuid: clientId,
                  contractGuid: guid,
                  data: {
                    ...contractBody,
                    createRecommended: !selected,
                  },
                });
              } catch {
                toast.error(t(Resources.Common.Error));
              }
            }}
          />
        </Box>
        <Typography variant="body2" color="secondary">
          {insuranceContract.product}
        </Typography>
      </Box>
      <NumberField
        value={insuranceContract.insuranceAmount.value}
        disabled={readOnly}
        name="compulsoryInsuranceCurrentAmount"
        onClick={(event) => {
          event.stopPropagation();
        }}
        onBlur={async (event) => {
          try {
            const { guid, ...contractBody } = insuranceContract;
            await updateInsuranceContract({
              analysisId,
              versionId,
              clientGuid: clientId,
              contractGuid: guid,
              data: {
                ...contractBody,
                insuranceAmount: {
                  ...insuranceContract.insuranceAmount,
                  value: Number(event.target.value.replaceAll(" ", "")),
                },
              },
            });
          } catch {
            toast.error(t(Resources.Common.Error));
          }
        }}
        min={0}
        thousandsSeparator=" "
        unit={
          // as wide in every tile, so the amounts line up whatever the frequency
          <Box component="span" minWidth={92} textAlign="right">
            {t(Resources.Common.CZKSuffix)}/{getAmountFrequencyTranslation(insuranceContract.insuranceAmount.frequency, t)}
          </Box>
        }
        slotProps={{ htmlInput: { "aria-label": insuranceContract.product ?? undefined } }}
      />
    </Stack>
  );
};
